import { useMemo, useState } from 'react';

import { useSort } from '../../../../shared/hooks/useSort';
import { usePagination } from '../../../../shared/hooks/usePagination';
import { paginationSmallListLimit } from '../../../../shared/constants/systemConstants';
import { ReleaseInfo } from '../../../interfaces/ReleaseInfo';
import { ReleaseFilterValues } from '../../../interfaces/ReleaseFilters';

const defaultReleaseFilterValues: ReleaseFilterValues = {
  platform: '',
  status: '',
};

const elementsPerPage = paginationSmallListLimit;

export const useReleasesList = (releases: ReleaseInfo[]) => {
  // Plain useState instead of react-hook-form: two selects filtering a static local list
  // don't need form machinery (watch/submit sync, `useAutoSubmitForm` + <form> wrapper),
  // and Clear All is just a state reset. Switch to RHF (mirror challenge-leaderboard
  // FiltersRow) once filters grow — e.g. a debounced search input, validation, or
  // submitting filter values to an API/store.
  const [filters, setFilters] = useState<ReleaseFilterValues>(defaultReleaseFilterValues);

  const filteredReleases = useMemo(
    () =>
      releases.filter(
        (release) =>
          (!filters.platform || release.platform === filters.platform) &&
          (!filters.status || release.status === filters.status),
      ),
    [releases, filters],
  );

  const { sortedData: sortedReleases, sortMethods } = useSort({
    data: filteredReleases,
    sortTypeConfig: { platform: 'STRING' },
  });

  const {
    paginatedData: visibleReleases,
    page,
    totalPages,
    handlePageChange,
    resetPage,
  } = usePagination(sortedReleases, elementsPerPage);

  const handleFiltersChange = (newFilters: ReleaseFilterValues) => {
    setFilters(newFilters);
    resetPage();
  };

  const handleReset = () => {
    setFilters(defaultReleaseFilterValues);
    resetPage();
  };

  const isFiltered = !!filters.platform || !!filters.status;

  return {
    filters,
    visibleReleases,
    sortMethods,
    page,
    totalPages,
    totalElements: sortedReleases.length,
    elementsPerPage,
    isFiltered,
    handleFiltersChange,
    handleReset,
    handlePageChange,
  };
};
